/**
 * Call It In LLC — Portal Access Management
 *
 * External portal user management surface for admin.
 *
 * Vendor reps (role = "vendor_rep") are EXTERNAL users who authenticate
 * through the Dashboard Portal.  They are NOT employees.  This page keeps
 * their lifecycle completely separate from the internal staff directory.
 *
 * Backend endpoints used:
 *   GET  /auth/users               → all users (filtered client-side to vendor_rep)
 *   POST /auth/users               → create { email, name, password, role="vendor_rep", organizationId }
 *   PUT  /auth/users/{id}          → update contact info / organizationId
 *   DELETE /auth/users/{id}        → soft-deactivate
 *   POST /auth/users/{id}/reactivate → reactivate
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";

import DashboardLayout from "../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../examples/Navbars/DashboardNavbar";
import MDBox from "../components/MDBox";
import MDTypography from "../components/MDTypography";
import MDButton from "../components/MDButton";
import MDInput from "../components/MDInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import ComplexStatisticsCard from "../examples/Cards/StatisticsCards/ComplexStatisticsCard";
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
} from "../api/client";

// ── Helpers ──────────────────────────────────────────────────────────────────

function hydrateRep(u) {
  // Defensive: log a warning if the backend did not supply a user id.
  // This surfaces misconfigured accounts early rather than silently sending
  // PUT /auth/users/undefined to the backend.
  if (!u?.id) {
    console.warn("[PortalAccess] hydrateRep received a user record without an id field:", u);
  }
  return {
    id:              u.id,               // UUID — must be present for Edit/Disable to work
    name:            u.name || u.email,
    email:           u.email,
    role:            u.role,
    is_active:       u.is_active ?? 1,
    created_at:      u.created_at,
    // GET /auth/users returns both snake_case columns AND camelCase aliases.
    // POST and PUT responses return camelCase only.  Handle both for safety.
    organizationId:   u.organizationId   || u.organization_id   || "",
    organizationName: u.organizationName || u.organization_name || "",
  };
}

/**
 * Convert an org name to a stable slug for the organizationId field.
 * Must produce identical output to slugFromName in task-create/index.js
 * so portal user scoping and task scoping always agree.
 *
 * Algorithm: strip non-alphanumeric chars (keep spaces), collapse spaces to
 * hyphens, trim edges.  Does NOT replace non-alphanumeric with hyphens
 * directly (that causes "st-mary-s" from "St. Mary's" instead of "st-marys").
 *
 * Examples:
 *   "Storage Plus"     → "storage-plus"
 *   "St. Mary's"       → "st-marys"
 *   "A&B Corp"         → "ab-corp"
 *   "Call It In LLC"   → "call-it-in-llc"
 */
function slugFromName(name) {
  if (!name) return "";
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")  // remove punctuation, keep spaces
    .replace(/\s+/g, "-")          // spaces → single hyphen
    .replace(/^-+|-+$/g, "")      // trim edge hyphens
    .slice(0, 60);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const s = String(iso);
    const normalized = s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s) ? s : s + "Z";
    return new Date(normalized).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

// ── Visual helpers ────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: "#94a3b8", mt: 1, mb: 0.25,
    }}>
      {children}
    </Typography>
  );
}

function AccessStatusBadge({ isActive }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 10,
      fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
      background: isActive ? "#ede9fe" : "#f1f5f9",
      color:      isActive ? "#7c3aed" : "#94a3b8",
      border:     `1px solid ${isActive ? "#c4b5fd" : "#e2e8f0"}`,
    }}>
      {isActive ? "Active" : "Disabled"}
    </span>
  );
}

function OrgIdBadge({ orgId }) {
  if (!orgId) return <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>—</span>;
  return (
    <span style={{
      display: "inline-block", padding: "1px 8px", borderRadius: 8,
      fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.04em",
      fontFamily: "monospace",
      background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0",
    }}>
      {orgId}
    </span>
  );
}

// ── Create Portal Access modal ────────────────────────────────────────────────

const EMPTY_ADD = {
  organizationName: "",
  organizationId: "",
  name: "",
  email: "",
  password: "",
  _saving: false,
};

function CreateAccessModal({ open, onClose, onCreated }) {
  const [form, setForm]   = useState(EMPTY_ADD);
  const [error, setError] = useState("");
  const [revealPwd, setRevealPwd] = useState(false);

  useEffect(() => {
    if (open) { setForm(EMPTY_ADD); setError(""); setRevealPwd(false); }
  }, [open]);

  const set = (key, val) =>
    setForm((prev) => {
      const next = { ...prev, [key]: val };
      // Auto-populate organizationId from organizationName unless already customized.
      if (key === "organizationName" && !prev._orgIdManuallySet) {
        next.organizationId = slugFromName(val);
      }
      return next;
    });

  const setOrgId = (val) =>
    setForm((prev) => ({ ...prev, organizationId: val, _orgIdManuallySet: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.organizationName.trim()) return setError("Organization name is required.");
    if (!form.organizationId.trim())   return setError("Organization ID is required.");
    if (!form.name.trim())             return setError("Contact name is required.");
    if (!form.email.trim())            return setError("Contact email is required.");
    if (!form.password.trim())         return setError("Password is required.");
    if (form.password.length < 8)      return setError("Password must be at least 8 characters.");

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const res  = await createUser({
        name:           form.name.trim(),
        email:          form.email.trim(),
        password:       form.password,
        role:           "vendor_rep",
        organizationId: form.organizationId.trim(),
        // Stored on the user record so it can be displayed without a separate
        // organizations table.  Remove when a dedicated /organizations endpoint exists.
        organizationName: form.organizationName.trim(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onCreated(data);
      } else if (res.status === 409) {
        setError(data?.detail ?? "An account with that email already exists.");
      } else {
        setError(data?.detail ?? `Failed to create portal access (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Create Portal Access
        <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontWeight: 400, mt: 0.25 }}>
          Grants a vendor / client rep login access to the Dashboard Portal.
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

          <SectionLabel>Organization / Client</SectionLabel>
          <MDInput
            label="Organization Name"
            value={form.organizationName}
            onChange={(e) => set("organizationName", e.target.value)}
            fullWidth required autoFocus
            disabled={form._saving}
            placeholder="e.g. Acme Orthopedics"
            helperText="The hospital, vendor, or medical device company this user belongs to."
          />
          <MDInput
            label="Organization ID"
            value={form.organizationId}
            onChange={(e) => setOrgId(e.target.value)}
            fullWidth required
            disabled={form._saving}
            placeholder="e.g. acme-orthopedics"
            helperText="Auto-generated from org name — you may customize. Used to scope case access."
            inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
          />

          <SectionLabel>Portal Contact</SectionLabel>
          <MDBox display="flex" gap={2}>
            <MDInput
              label="Contact Full Name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              fullWidth required
              disabled={form._saving}
            />
            <MDInput
              type="email"
              label="Contact Email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              fullWidth required
              disabled={form._saving}
            />
          </MDBox>

          <SectionLabel>Access Credentials</SectionLabel>
          <MDInput
            type={revealPwd ? "text" : "password"}
            label="Password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            fullWidth required
            disabled={form._saving}
            helperText="Minimum 8 characters. Share securely with the vendor contact."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setRevealPwd((v) => !v)} size="small" edge="end">
                    <Icon sx={{ fontSize: "1.1rem !important" }}>
                      {revealPwd ? "visibility_off" : "visibility"}
                    </Icon>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info"
              disabled={form._saving} sx={{ minWidth: 160 }}>
              {form._saving
                ? <CircularProgress size={18} color="inherit" />
                : "Create Portal Access"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Portal User modal ────────────────────────────────────────────────────

function EditPortalUserModal({ user, open, onClose, onSaved }) {
  const [form, setForm]   = useState({});
  const [error, setError] = useState("");
  const [revealPwd, setRevealPwd] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name:             user.name,
        email:            user.email,
        organizationId:   user.organizationId || "",
        organizationName: user.organizationName || "",
        password:         "",
        _saving:          false,
      });
      setError("");
      setRevealPwd(false);
    }
  }, [user]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim())  return setError("Contact name is required.");
    if (!form.email.trim()) return setError("Contact email is required.");
    if (form.password && form.password.length < 8)
      return setError("New password must be at least 8 characters.");

    // Guard: user.id must be the UUID from the backend. If it's missing we
    // would silently call PUT /auth/users/undefined and get a 404 back.
    if (!user?.id) {
      console.error("[EditPortalUserModal] user.id is undefined — cannot submit PUT.", user);
      return setError("Cannot save: this user record is missing its ID. Please refresh the page and try again.");
    }

    console.log("[EditPortalUserModal] PUT /auth/users/" + user.id, "user:", user);

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const payload = {
        name:             form.name.trim(),
        email:            form.email.trim(),
        organizationId:   form.organizationId.trim() || null,
        organizationName: form.organizationName.trim() || null,
      };
      if (form.password.trim()) payload.password = form.password.trim();

      const res  = await updateUser(user.id, payload);
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        onSaved({ ...user, ...data });
      } else if (res.status === 409) {
        setError("That email address is already in use by another account.");
      } else {
        setError(data?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Edit Portal User
        {user && (
          <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontWeight: 400, mt: 0.25 }}>
            {user.email}
          </Typography>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

          <SectionLabel>Organization</SectionLabel>
          <MDBox display="flex" gap={2}>
            <MDInput
              label="Organization Name"
              value={form.organizationName || ""}
              onChange={(e) => set("organizationName", e.target.value)}
              fullWidth
              disabled={form._saving}
            />
            <MDInput
              label="Organization ID"
              value={form.organizationId || ""}
              onChange={(e) => set("organizationId", e.target.value)}
              fullWidth
              disabled={form._saving}
              inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
            />
          </MDBox>

          <SectionLabel>Contact Identity</SectionLabel>
          <MDBox display="flex" gap={2}>
            <MDInput
              label="Contact Full Name"
              value={form.name || ""}
              onChange={(e) => set("name", e.target.value)}
              fullWidth required
              disabled={form._saving}
            />
            <MDInput
              type="email"
              label="Contact Email"
              value={form.email || ""}
              onChange={(e) => set("email", e.target.value)}
              fullWidth required
              disabled={form._saving}
            />
          </MDBox>

          <SectionLabel>Password Reset (optional)</SectionLabel>
          <MDInput
            type={revealPwd ? "text" : "password"}
            label="New Password — leave blank to keep current"
            value={form.password || ""}
            onChange={(e) => set("password", e.target.value)}
            fullWidth
            disabled={form._saving}
            helperText={form.password ? "Minimum 8 characters" : "Leave blank to keep existing password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setRevealPwd((v) => !v)} size="small" edge="end">
                    <Icon sx={{ fontSize: "1.1rem !important" }}>
                      {revealPwd ? "visibility_off" : "visibility"}
                    </Icon>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info"
              disabled={form._saving} sx={{ minWidth: 140 }}>
              {form._saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, body, confirmLabel, confirmColor = "error", onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined}
      maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>{body}</MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color={confirmColor} onClick={onConfirm}
          disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={16} color="inherit" /> : confirmLabel}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PortalAccess() {
  const [allReps, setAll]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [listError, setError]   = useState("");

  // Filters
  const [statusFilter, setStatus] = useState("active");
  const [search, setSearch]       = useState("");

  // Modals
  const [createOpen, setCreateOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [confirmDialog, setConfirm]   = useState({
    open: false, type: null, user: null, loading: false, error: "",
  });

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchReps = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res  = await listUsers();
      const data = await res.json().catch(() => null);
      if (res.ok && Array.isArray(data)) {
        // Only keep vendor_rep role — strictly separated from employees
        setAll(data.filter((u) => u.role === "vendor_rep").map(hydrateRep));
      } else {
        setError(data?.detail ?? `Failed to load portal users (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReps(); }, [fetchReps]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active   = allReps.filter((u) => u.is_active === 1 || u.is_active === true).length;
    const inactive = allReps.length - active;
    const orgs     = new Set(allReps.map((u) => u.organizationId).filter(Boolean)).size;
    return { total: allReps.length, active, inactive, orgs };
  }, [allReps]);

  // ── Filtered list ─────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allReps.filter((u) => {
      if (statusFilter === "active"   && !(u.is_active === 1 || u.is_active === true)) return false;
      if (statusFilter === "inactive" && (u.is_active === 1 || u.is_active === true))  return false;
      if (q && !u.name.toLowerCase().includes(q) &&
               !u.email.toLowerCase().includes(q) &&
               !u.organizationId.toLowerCase().includes(q) &&
               !u.organizationName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allReps, statusFilter, search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreated = (raw) => {
    setCreateOpen(false);
    const rep = hydrateRep({ ...raw, role: "vendor_rep" });
    setAll((prev) => [rep, ...prev]);
  };

  const handleSaved = (updated) => {
    setEditTarget(null);
    setAll((prev) => prev.map((u) => (u.id === updated.id ? hydrateRep(updated) : u)));
  };

  const openConfirm = (type, user) =>
    setConfirm({ open: true, type, user, loading: false, error: "" });

  const closeConfirm = () =>
    setConfirm((p) => ({ ...p, open: false }));

  const handleConfirmAction = async () => {
    const { type, user } = confirmDialog;
    setConfirm((p) => ({ ...p, loading: true, error: "" }));
    try {
      let res;
      if (type === "disable")     res = await deactivateUser(user.id);
      else if (type === "enable") res = await reactivateUser(user.id);
      else return;

      if (res.ok) {
        const isActive = type === "enable" ? 1 : 0;
        setAll((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: isActive } : u));
        closeConfirm();
      } else {
        const data = await res.json().catch(() => ({}));
        setConfirm((p) => ({ ...p, loading: false, error: data?.detail ?? `Action failed (HTTP ${res.status}).` }));
      }
    } catch {
      setConfirm((p) => ({ ...p, loading: false, error: "Cannot reach the server." }));
    }
  };

  // ── Confirm dialog content ────────────────────────────────────────────────
  const confirmContent = useMemo(() => {
    const { type, user, error: cErr } = confirmDialog;
    if (!user) return {};
    if (type === "disable") {
      return {
        title: "Disable Portal Access",
        body: (
          <>
            <Typography variant="body2">
              <strong>{user.name}</strong> ({user.email}) will no longer be able to log into the Dashboard Portal.
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              You can re-enable access at any time.
            </Typography>
            {cErr && <Alert severity="error" sx={{ mt: 1 }}>{cErr}</Alert>}
          </>
        ),
        confirmLabel: "Disable Access",
        confirmColor: "error",
      };
    }
    if (type === "enable") {
      return {
        title: "Re-enable Portal Access",
        body: (
          <>
            <Typography variant="body2">
              Restore portal login access for <strong>{user.name}</strong> ({user.email}).
            </Typography>
            {cErr && <Alert severity="error" sx={{ mt: 1 }}>{cErr}</Alert>}
          </>
        ),
        confirmLabel: "Enable Access",
        confirmColor: "success",
      };
    }
    return {};
  }, [confirmDialog]);

  // ── Table row ──────────────────────────────────────────────────────────────
  const COL = {
    label: { fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em",
             textTransform: "uppercase", color: "#94a3b8", padding: "10px 12px" },
    cell:  { padding: "10px 12px", fontSize: "0.82rem", verticalAlign: "middle" },
  };

  const TableHeader = () => (
    <thead>
      <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
        {["Organization", "Contact", "Email", "Status", "Created", "Actions"].map((h) => (
          <th key={h} style={{ ...COL.label, textAlign: "left" }}>{h}</th>
        ))}
      </tr>
    </thead>
  );

  const RepRow = ({ rep }) => {
    const isActive = rep.is_active === 1 || rep.is_active === true;

    const handleEditClick = () => {
      // Verify the full user object — including the UUID — before opening the modal.
      // This log is intentional for debugging: it lets us confirm that rep.id is the
      // correct UUID and not undefined, an email, or an org slug.
      console.log("[PortalAccess] Edit clicked — user object:", rep);
      if (!rep.id) {
        console.error("[PortalAccess] rep.id is missing! Cannot send PUT /auth/users/undefined.", rep);
      }
      setEditTarget(rep);
    };

    const handleDisableClick = () => {
      console.log("[PortalAccess] Disable clicked — user.id:", rep.id, "user:", rep);
      openConfirm("disable", rep);
    };

    const handleEnableClick = () => {
      console.log("[PortalAccess] Enable clicked — user.id:", rep.id, "user:", rep);
      openConfirm("enable", rep);
    };

    return (
      <tr style={{ borderBottom: "1px solid #f1f5f9", opacity: isActive ? 1 : 0.6 }}>
        <td style={COL.cell}>
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block", color: "#1e293b" }}>
              {rep.organizationName || "—"}
            </MDTypography>
            <OrgIdBadge orgId={rep.organizationId} />
          </MDBox>
        </td>
        <td style={COL.cell}>
          <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#334155" }}>
            {rep.name}
          </MDTypography>
        </td>
        <td style={COL.cell}>
          <MDTypography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace" }}>
            {rep.email}
          </MDTypography>
        </td>
        <td style={COL.cell}>
          <AccessStatusBadge isActive={isActive} />
        </td>
        <td style={COL.cell}>
          <MDTypography variant="caption" sx={{ color: "#94a3b8" }}>
            {fmtDate(rep.created_at)}
          </MDTypography>
        </td>
        <td style={COL.cell}>
          <MDBox display="flex" gap={0.75} alignItems="center">
            <Tooltip title="Edit portal user" arrow>
              <span>
                <MDButton variant="outlined" color="info" size="small"
                  onClick={handleEditClick}
                  sx={{ minWidth: 0, px: 1.25, py: 0.5, fontSize: "0.72rem" }}>
                  Edit
                </MDButton>
              </span>
            </Tooltip>
            {isActive ? (
              <Tooltip title="Disable portal login" arrow>
                <span>
                  <MDButton variant="outlined" color="error" size="small"
                    onClick={handleDisableClick}
                    sx={{ minWidth: 0, px: 1.25, py: 0.5, fontSize: "0.72rem" }}>
                    Disable
                  </MDButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Re-enable portal login" arrow>
                <span>
                  <MDButton variant="outlined" color="success" size="small"
                    onClick={handleEnableClick}
                    sx={{ minWidth: 0, px: 1.25, py: 0.5, fontSize: "0.72rem" }}>
                    Enable
                  </MDButton>
                </span>
              </Tooltip>
            )}
          </MDBox>
        </td>
      </tr>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <MDBox mb={3} display="flex" alignItems="flex-start" justifyContent="space-between"
          flexWrap="wrap" gap={2}>
          <MDBox>
            <MDTypography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.01em", color: "#0f172a" }}>
              Portal Access
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
              External vendor &amp; client users — separate from internal employees
            </MDTypography>
            {/* Explicit separation notice */}
            <MDBox mt={0.75} display="inline-flex" alignItems="center" gap={0.5}
              sx={{
                px: 1.5, py: 0.4, borderRadius: 1, fontSize: "0.7rem", fontWeight: 700,
                background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a",
                letterSpacing: "0.04em", textTransform: "uppercase",
              }}>
              <Icon sx={{ fontSize: "0.9rem !important" }}>info</Icon>
              External portal users only — not staff or drivers
            </MDBox>
          </MDBox>
          <MDButton variant="gradient" color="info" onClick={() => setCreateOpen(true)}
            startIcon={<Icon>add</Icon>} sx={{ alignSelf: "flex-start" }}>
            Create Portal Access
          </MDButton>
        </MDBox>

        {/* ── Stats row ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} mb={3}>
          {[
            { title: "Portal Users",   count: loading ? "—" : stats.total,    icon: "badge",          color: "info" },
            { title: "Active Access",  count: loading ? "—" : stats.active,   icon: "check_circle",   color: "success" },
            { title: "Disabled",       count: loading ? "—" : stats.inactive, icon: "block",          color: "warning" },
            { title: "Organizations",  count: loading ? "—" : stats.orgs,     icon: "business",       color: "primary" },
          ].map(({ title, count, icon, color }) => (
            <Grid item xs={12} sm={6} xl={3} key={title}>
              <ComplexStatisticsCard
                color={color}
                icon={icon}
                title={title}
                count={count}
                percentage={{ color: "text", label: "" }}
              />
            </Grid>
          ))}
        </Grid>

        {/* ── Main card ─────────────────────────────────────────────────── */}
        <Card sx={{ overflow: "hidden" }}>
          {/* Filters */}
          <MDBox px={3} py={2} display="flex" alignItems="center" gap={2} flexWrap="wrap"
            sx={{ borderBottom: "1px solid #f1f5f9" }}>
            <MDInput
              placeholder="Search org, name, or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ fontSize: "1rem !important", color: "#94a3b8" }}>search</Icon>
                  </InputAdornment>
                ),
              }}
            />

            <MDBox display="flex" gap={1} flexWrap="wrap">
              {[
                { key: "active",   label: "Active" },
                { key: "inactive", label: "Disabled" },
                { key: "all",      label: "All" },
              ].map(({ key, label }) => (
                <MDButton key={key} variant={statusFilter === key ? "gradient" : "outlined"}
                  color={statusFilter === key ? "info" : "secondary"}
                  size="small"
                  onClick={() => setStatus(key)}
                  sx={{ minWidth: 70, py: 0.6, fontSize: "0.72rem" }}>
                  {label}
                </MDButton>
              ))}
            </MDBox>

            <MDButton variant="outlined" color="secondary" size="small"
              onClick={fetchReps} disabled={loading}
              sx={{ ml: "auto", minWidth: 0, px: 1.5, py: 0.6 }}>
              <Icon sx={{ fontSize: "1rem !important" }}>refresh</Icon>
            </MDButton>
          </MDBox>

          {/* Error banner */}
          {listError && (
            <MDBox px={3} pt={2}>
              <Alert severity="error" onClose={() => setError("")}>{listError}</Alert>
            </MDBox>
          )}

          {/* Table */}
          <MDBox sx={{ overflowX: "auto" }}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress size={28} />
                <MDTypography variant="body2" sx={{ ml: 2, color: "#94a3b8" }}>
                  Loading portal users…
                </MDTypography>
              </MDBox>
            ) : visible.length === 0 ? (
              <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
                <Icon sx={{ fontSize: "2.5rem !important", color: "#cbd5e1" }}>badge</Icon>
                <MDTypography variant="body2" sx={{ color: "#94a3b8" }}>
                  {allReps.length === 0
                    ? "No portal users yet — create the first one above."
                    : "No portal users match the current filter."}
                </MDTypography>
              </MDBox>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <TableHeader />
                <tbody>
                  {visible.map((rep) => <RepRow key={rep.id} rep={rep} />)}
                </tbody>
              </table>
            )}
          </MDBox>

          {/* Footer count */}
          {!loading && visible.length > 0 && (
            <MDBox px={3} py={1.5} sx={{ borderTop: "1px solid #f1f5f9" }}>
              <MDTypography variant="caption" sx={{ color: "#94a3b8" }}>
                Showing {visible.length} of {allReps.length} portal user{allReps.length !== 1 ? "s" : ""}
              </MDTypography>
            </MDBox>
          )}
        </Card>

      </MDBox>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <CreateAccessModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <EditPortalUserModal
        user={editTarget}
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        onSaved={handleSaved}
      />

      {confirmContent.title && (
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmContent.title}
          body={confirmContent.body}
          confirmLabel={confirmContent.confirmLabel}
          confirmColor={confirmContent.confirmColor}
          onConfirm={handleConfirmAction}
          onClose={closeConfirm}
          loading={confirmDialog.loading}
        />
      )}
    </DashboardLayout>
  );
}
